(function twoNuActivityMatchModule() {
  "use strict";

  if (customElements.get("two-nu-activity-match")) return;

  const parseJson = (node, fallback) => {
    try {
      return JSON.parse(node?.textContent || "");
    } catch (error) {
      console.error("2nu activity match JSON could not be parsed.", error);
      return fallback;
    }
  };

  const sameValues = (left, right) => (
    Array.isArray(left)
    && Array.isArray(right)
    && left.length === right.length
    && left.every((value, index) => value === right[index])
  );

  const withCount = (template, count) => String(template || "")
    .replace(/__COUNT__/g, String(count))
    .replace(/\{\{\s*count\s*\}\}/g, String(count));

  const ACTIVITY_DEFAULT_FAMILY_ORDER = ["hex", "tvo_pro"];
  const MULTI_ACTIVITY_DEFAULT_FAMILY_ORDER = ["hex", "tvo_pro", "tvo"];
  const PRESCRIPTION_FAMILIES = new Set(["pre", "hex_pre"]);
  const ACTIVITY_SCORE_KEYS = Object.freeze([
    "running",
    "trail",
    "water",
    "cycling",
    "driving",
    "golf",
    "everyday",
    "alpine",
  ]);
  const NON_HEX_PRE_FAMILY = (variant) => (
    Array.isArray(variant?.families)
      ? variant.families.find((familyKey) => familyKey !== "hex_pre") || ""
      : ""
  );

  const preferredActivityVariant = (rankedVariants) => {
    const available = (Array.isArray(rankedVariants) ? rankedVariants : [])
      .filter((entry) => entry?.variant?.available === true);
    for (const familyKey of ACTIVITY_DEFAULT_FAMILY_ORDER) {
      const match = available.find((entry) => (
        Array.isArray(entry.variant.families)
        && entry.variant.families.includes(familyKey)
      ));
      if (match) return { entry: match, familyKey };
    }
    const fallback = available.find((entry) => NON_HEX_PRE_FAMILY(entry.variant));
    return fallback
      ? { entry: fallback, familyKey: NON_HEX_PRE_FAMILY(fallback.variant) }
      : { entry: null, familyKey: "" };
  };

  const hasValidActivityScoreShape = (variant) => {
    const scores = variant?.activityScores;
    if (!scores || typeof scores !== "object" || Array.isArray(scores)) return false;
    const scoreKeys = Object.keys(scores);
    if (
      scoreKeys.length !== ACTIVITY_SCORE_KEYS.length
      || scoreKeys.some((key) => !ACTIVITY_SCORE_KEYS.includes(key))
    ) {
      return false;
    }
    return ACTIVITY_SCORE_KEYS.every((key) => (
      Number.isFinite(scores[key])
      && scores[key] >= 0
      && scores[key] <= 5
    ));
  };

  const preferredMultiActivityVariant = (rankedVariants) => {
    const safeAvailable = (Array.isArray(rankedVariants) ? rankedVariants : [])
      .filter((entry) => {
        const variant = entry?.variant;
        const families = Array.isArray(variant?.families) ? variant.families : [];
        return variant?.available === true
          && !families.some((familyKey) => PRESCRIPTION_FAMILIES.has(familyKey));
      });
    for (const familyKey of MULTI_ACTIVITY_DEFAULT_FAMILY_ORDER) {
      const match = safeAvailable.find((entry) => entry.variant.families.includes(familyKey));
      if (match) return { entry: match, familyKey };
    }
    return { entry: null, familyKey: "" };
  };

  class TwoNuActivityMatch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.twoNuActivityMatchReady === "true") return;
      this.core = window.TwoNuActivityCore;
      this.config = parseJson(this.querySelector("[data-two-nu-activity-match-config]"), {});
      this.registry = parseJson(this.querySelector("[data-two-nu-activity-registry]"), { destinations: {} });
      this.status = this.querySelector("[data-two-nu-activity-match-status]");
      this.empty = this.querySelector("[data-two-nu-activity-match-empty]");
      this.productSelector = this.dataset.productSectionSelector || "[data-callout-lens-family-v2]";
      this.dataset.twoNuActivityMatchReady = "true";
      this.initDock();
      this.apply();
    }

    dockTargetFor(link) {
      const selector = link?.dataset.twoNuActivityDockTarget || "";
      if (!selector.startsWith("#")) return null;
      return document.getElementById(selector.slice(1));
    }

    setDockActive(activeLink) {
      this.dockLinks.forEach((link) => {
        const isActive = link === activeLink;
        link.classList.toggle("is-active", isActive);
        if (isActive) link.setAttribute("aria-current", "location");
        else link.removeAttribute("aria-current");
      });
    }

    updateDockActiveFromPosition() {
      if (!this.dockItems?.length) return;
      const dockSystem = window.twoNuDockSystem || {};
      const viewport = typeof dockSystem.getViewportState === "function"
        ? dockSystem.getViewportState()
        : { height: window.innerHeight || 0 };
      const viewportHeight = viewport.height || window.innerHeight || 0;
      const triggerLine = Math.min(160, Math.max(136, viewportHeight * 0.16));
      let activeItem = this.dockItems[0];

      this.dockItems.forEach((item) => {
        if (item.target.getBoundingClientRect().top <= triggerLine) activeItem = item;
      });

      const documentHeight = document.documentElement?.scrollHeight || document.body?.scrollHeight || 0;
      if (window.scrollY + viewportHeight >= documentHeight - 2) {
        activeItem = this.dockItems[this.dockItems.length - 1];
      }
      this.setDockActive(activeItem.link);
    }

    initDock() {
      this.dock = this.querySelector("[data-two-nu-activity-match-dock]");
      if (!this.dock) return;

      this.dockLinks = Array.from(this.dock.querySelectorAll("[data-two-nu-activity-dock-target]"));
      this.dockItems = this.dockLinks
        .map((link) => ({ link, target: this.dockTargetFor(link) }))
        .filter((item) => item.target);
      if (!this.dockItems.length) return;

      this.reducedMotionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)") || { matches: false };
      this.handleDockClick = (event) => {
        const link = event.currentTarget;
        const target = this.dockTargetFor(link);
        const selector = link.dataset.twoNuActivityDockTarget;
        if (!target || !selector) return;
        event.preventDefault();
        this.setDockActive(link);
        target.scrollIntoView({
          behavior: this.reducedMotionQuery.matches ? "auto" : "smooth",
          block: "start",
        });
        if (window.location.hash !== selector && window.history?.pushState) {
          window.history.pushState(null, "", selector);
        }
        window.setTimeout(
          () => this.updateDockActiveFromPosition(),
          this.reducedMotionQuery.matches ? 0 : 480
        );
      };
      this.dockLinks.forEach((link) => link.addEventListener("click", this.handleDockClick));

      this.handleDockViewportChange = () => this.updateDockActiveFromPosition();
      window.addEventListener("resize", this.handleDockViewportChange);
      window.addEventListener("twoNu:viewportchange", this.handleDockViewportChange);

      this.handleDockScroll = () => {
        if (this.dockScrollFrame) return;
        this.dockScrollFrame = window.requestAnimationFrame(() => {
          this.dockScrollFrame = null;
          this.updateDockActiveFromPosition();
        });
      };
      window.addEventListener("scroll", this.handleDockScroll, { passive: true });

      this.updateDockActiveFromPosition();
    }

    disconnectedCallback() {
      this.lazyImageObserver?.disconnect();
      this.dockLinks?.forEach((link) => link.removeEventListener("click", this.handleDockClick));
      if (this.handleDockViewportChange) {
        window.removeEventListener("resize", this.handleDockViewportChange);
        window.removeEventListener("twoNu:viewportchange", this.handleDockViewportChange);
      }
      if (this.handleDockScroll) window.removeEventListener("scroll", this.handleDockScroll);
      if (this.dockScrollFrame) window.cancelAnimationFrame(this.dockScrollFrame);
    }

    markDownstreamImagesLazy(root) {
      if (!root) return;
      root.querySelectorAll("img").forEach((image) => {
        image.loading = "lazy";
        image.setAttribute("fetchpriority", "low");
        if (!image.decoding) image.decoding = "async";
      });

      if (!("MutationObserver" in window)) return;
      if (!this.lazyImageObserver) {
        this.lazyImageObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (!(node instanceof Element)) return;
              const images = node.matches("img") ? [node] : Array.from(node.querySelectorAll("img"));
              images.forEach((image) => {
                image.loading = "lazy";
                image.setAttribute("fetchpriority", "low");
                if (!image.decoding) image.decoding = "async";
              });
            });
          });
        });
      }
      this.lazyImageObserver.observe(root, { childList: true, subtree: true });
    }

    failClosed(calloutRoot, message) {
      if (calloutRoot) calloutRoot.hidden = true;
      if (this.status) this.status.textContent = message || this.config.i18n?.configurationMissing || "";
      if (this.empty) this.empty.hidden = true;
      this.dataset.activityMatchState = "configuration-error";
    }

    apply() {
      const calloutRoot = document.querySelector(this.productSelector);
      this.markDownstreamImagesLazy(this.querySelector("[data-two-nu-activity-match-answer]"));
      this.markDownstreamImagesLazy(calloutRoot);
      if (!this.core || !calloutRoot || this.config.configurationValid !== true) {
        this.failClosed(calloutRoot);
        return;
      }

      const selectedActivityIds = this.core.canonicalizeActivityIds(this.config.activityIds, { min: 1, max: 3 });
      if (selectedActivityIds.length !== this.config.activityIds.length) {
        this.failClosed(calloutRoot);
        return;
      }
      const isMulti = selectedActivityIds.length >= 2;
      calloutRoot.dataset.calloutActivityMatchMulti = isMulti ? "true" : "false";

      const expectedScoreKeys = this.core.scoreKeysFor(selectedActivityIds);
      if (!sameValues(expectedScoreKeys, this.config.scoreKeysDeduped)) {
        this.failClosed(calloutRoot);
        return;
      }

      const serverFilterRequested = calloutRoot.dataset.calloutActivityMatchFilterRequested === "true";
      const serverFilterActive = calloutRoot.dataset.calloutActivityMatchFilterActive === "true";
      if (!serverFilterRequested || !serverFilterActive) {
        this.failClosed(calloutRoot);
        return;
      }

      const registryDestination = this.core.destinationFor(this.registry, selectedActivityIds);
      if (
        isMulti
        && (
          !registryDestination
          || registryDestination.status !== "ready"
          || !["pair", "triple"].includes(registryDestination.kind)
          || !sameValues(registryDestination.selectedActivityIds, selectedActivityIds)
          || !sameValues(registryDestination.scoreKeysDeduped, expectedScoreKeys)
          || !Number.isFinite(registryDestination.threshold?.minTotal)
          || !Number.isFinite(registryDestination.threshold?.minEach)
          || !Number.isFinite(this.config.threshold)
          || registryDestination.threshold.minTotal !== this.config.threshold
        )
      ) {
        this.failClosed(calloutRoot);
        return;
      }
      const destination = Object.assign({}, registryDestination, {
        threshold: Object.assign({}, registryDestination?.threshold || {}, { minTotal: Number(this.config.threshold) }),
      });
      const items = Array.from(calloutRoot.querySelectorAll("[data-callout-lens-family-v2-item]"));
      const rankedModels = [];

      items.forEach((item, modelOrder) => {
        const dataNode = item.querySelector("[data-callout-lens-family-v2-data]");
        const data = parseJson(dataNode, {});
        const sourceVariants = Array.isArray(data.variants) ? data.variants : [];
        const rankableVariants = isMulti
          ? sourceVariants.filter(hasValidActivityScoreShape)
          : data.variants;
        const rankedVariants = this.core.rankVariants(rankableVariants, selectedActivityIds, destination);
        if (!rankedVariants.length) {
          item.hidden = true;
          item.setAttribute("aria-hidden", "true");
          return;
        }

        const activityDefault = isMulti
          ? preferredMultiActivityVariant(rankedVariants)
          : preferredActivityVariant(rankedVariants);
        if (isMulti && !activityDefault.entry) {
          item.hidden = true;
          item.setAttribute("aria-hidden", "true");
          delete item.dataset.calloutV2ActivityPreferredVariantId;
          return;
        }

        const eligibleIds = new Set(rankedVariants.map((entry) => String(entry.variant.id)));
        const rankIndexById = new Map(rankedVariants.map((entry, index) => [String(entry.variant.id), index]));
        const eligibleFamilies = new Set(rankedVariants.flatMap((entry) => entry.variant.families || []));

        item.querySelectorAll(".swatch-wrapper").forEach((swatch) => {
          const source = swatch.dataset.activityMatchSourceIds
            || swatch.dataset.calloutV2OriginalIds
            || swatch.dataset.ids
            || "";
          if (!swatch.dataset.activityMatchSourceIds) swatch.dataset.activityMatchSourceIds = source;
          const eligibleSwatchIds = source
            .split(",")
            .map((id) => id.trim())
            .filter((id) => eligibleIds.has(id))
            .sort((left, right) => rankIndexById.get(left) - rankIndexById.get(right));
          const value = eligibleSwatchIds.join(",");
          swatch.dataset.calloutV2OriginalIds = value;
          swatch.dataset.ids = value;
          swatch.hidden = eligibleSwatchIds.length === 0;
          swatch.classList.toggle("callout-lens-family-v2__swatch-hidden", eligibleSwatchIds.length === 0);
        });

        item.querySelectorAll("[data-callout-v2-family]").forEach((button) => {
          button.hidden = !eligibleFamilies.has(button.dataset.calloutV2Family);
        });

        const best = rankedVariants[0];
        const preferredVariantId = activityDefault.entry?.variant?.id;
        data.activityMatchPreferredVariantId = preferredVariantId == null
          ? ""
          : String(preferredVariantId);
        data.activityMatchRankedVariantIds = rankedVariants.map((entry) => String(entry.variant.id));
        data.activityMatchIsMulti = isMulti;
        if (activityDefault.familyKey) data.defaultFamily = activityDefault.familyKey;
        data.variants = rankedVariants.map((entry) => entry.variant);
        dataNode.textContent = JSON.stringify(data);
        if (data.activityMatchPreferredVariantId) {
          item.dataset.calloutV2ActivityPreferredVariantId = data.activityMatchPreferredVariantId;
        } else {
          delete item.dataset.calloutV2ActivityPreferredVariantId;
        }
        item.hidden = false;
        item.removeAttribute("aria-hidden");
        item.dataset.activityMatchRank = String(rankedModels.length + 1);
        rankedModels.push({
          item,
          modelOrder,
          best: Object.assign({}, best, { stableOrder: modelOrder }),
        });
      });

      rankedModels.sort((left, right) => this.core.compareRanked(left.best, right.best));
      const grid = calloutRoot.querySelector(".callout-lens-family-v2__grid");
      rankedModels.forEach((model, index) => {
        model.item.dataset.activityMatchRank = String(index + 1);
        grid?.appendChild(model.item);
      });

      calloutRoot.hidden = rankedModels.length === 0;
      this.empty.hidden = rankedModels.length !== 0;
      this.status.textContent = rankedModels.length === 0
        ? this.config.i18n?.noResults
        : withCount(
          rankedModels.length === 1 ? this.config.i18n?.resultOne : this.config.i18n?.resultMany,
          rankedModels.length
        );
      this.dataset.activityMatchState = rankedModels.length ? "ready" : "empty";

      calloutRoot.dispatchEvent(new CustomEvent("two-nu:activity-match:applied", {
        bubbles: true,
        detail: {
          selectedActivityIds,
          isMulti,
          scoreKeysDeduped: expectedScoreKeys,
          modelCount: rankedModels.length,
        },
      }));
    }
  }

  customElements.define("two-nu-activity-match", TwoNuActivityMatch);
})();
